import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { FormHelper } from '../helpers/form.helper';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, map, merge, Observable, Subscription, take } from 'rxjs';
import { AuthenticatedUserService, SecurityModel } from '@tailormap-viewer/api';
import { EditModelHelper } from '../helpers/edit-model.helper';
import { ViewerEditFormFieldModel } from '../models/viewer-edit-form-field.model';
import { DateTime } from 'luxon';
import { EditFormInput } from '../models/edit-form-input.model';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { SelectFieldComponent } from '../fields/select-field/select-field.component';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
    selector: 'tm-edit-form',
    templateUrl: './edit-form.component.html',
    styleUrls: ['./edit-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepicker,
        SelectFieldComponent,
        MatCheckbox,
    ],
})
export class EditFormComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private authenticatedUserService = inject(AuthenticatedUserService);

  private _input: EditFormInput | undefined;

  private currentFormSubscription: Subscription | undefined;
  public formConfig: ViewerEditFormFieldModel[] = [];
  public userDetails$: Observable<SecurityModel | null>;

  @Input({ required: true })
  public set input(input: EditFormInput | undefined) {
    this._input = input;
    this.layerId = input?.details?.id || '';
    this.createForm();
  }
  public get input(): EditFormInput | undefined {
    return this._input;
  }

  @Output()
  public featureAttributeChanged = new EventEmitter<{ attribute: string; value: any; invalid?: boolean }>();

  @Output()
  public clearUniqueValueCacheAfterSave = new EventEmitter<string>();

  public form: FormGroup = new FormGroup({});

  public layerId: string = '';

  constructor() {
    this.userDetails$ = this.authenticatedUserService.getUserDetails$();
  }

  public ngOnDestroy() {
    if (this.currentFormSubscription) {
      this.currentFormSubscription.unsubscribe();
    }
  }

  private createForm() {
    if (this.currentFormSubscription) {
      this.currentFormSubscription.unsubscribe();
    }
    if (!this.input?.details || !this.input?.feature) {
      return;
    }
    this.formConfig = EditModelHelper.createEditModel(
      this.input.feature.feature,
      this.input.details,
      this.input.feature.columnMetadata,
      this.input.isNewFeature ?? false,
    );
    this.form = FormHelper.createForm(this.formConfig);

    const changes$ = Object.keys(this.form.controls)
      .map(key => {
        const control = this.form.get(key);
        if (control) {
          return control.valueChanges.pipe(
            debounceTime(250),
            map(value => [ key, value ]),
          );
        }
        return null;
      })
      .filter((valueChanges$): valueChanges$ is FormGroup['valueChanges'] => !!valueChanges$);
    this.currentFormSubscription = merge(...changes$)
      .subscribe(([ changedKey, value ]) => {
        const val = FormHelper.getFormValue(value);
        this.featureAttributeChanged.emit({ attribute: changedKey, value: val, invalid: !this.form.valid });
      });

    this.userDetails$.pipe(take(1)).subscribe(userDetails => {
      // Auto-fill fields with username/date/timestamp
      this.formConfig.forEach(field => {
        const control = this.form.get(field.name);
        if (control) {
          if (field.autoFillUser) {
            control.setValue(userDetails?.username ?? '');
          }
          if (field.autoFillDate) {
            if (field.type === 'date') {
              control.setValue(DateTime.now().toISODate());
            } else if (field.type === 'timestamp') {
              control.setValue(DateTime.now().toISO());
            }
          }
        }
      });
    });

    this.form.markAllAsTouched();
    this.cdr.detectChanges();
  }

  public getControl(name: string): FormControl {
    const control = this.form.get(name);
    if (!control) {
      throw new Error(`Control with name ${name} not found`);
    }
    return control as FormControl;
  }
}
